import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { parseEventOutputSchema } from '@awwyeah/shared';
import { Fireworks } from '../../../components/Fireworks';
import { useAiQuick } from '../../ai';
import { draftPatchFromProposal, quickAddContext, quickAddNote } from '../quickAdd';
import type { EventDraft } from './EventFormSheet';

/**
 * "Just say it" — one sentence, typed or dictated with the keyboard's mic, fills in the
 * new-event form (`quickAdd.ts`). The form below stays the source of truth: this only
 * patches the draft, and nothing is saved until Save.
 *
 * An assistant that's down is not an error (docs/AI-PIPELINE.md): the box says it's
 * napping and the form underneath works exactly as it always has.
 */
export function QuickAddBox({
  members,
  onFill,
}: {
  members: readonly { id: string; name: string }[];
  onFill: (patch: Partial<EventDraft>) => void;
}): JSX.Element {
  const [text, setText] = useState('');
  const [note, setNote] = useState<string | null>(null);
  /** Bumped on every successful fill, so saying it again sets off a fresh burst. */
  const [burst, setBurst] = useState(0);
  const { state, ask } = useAiQuick('parse-event');
  const thinking = state.phase === 'thinking';

  const submit = async (): Promise<void> => {
    const sentence = text.trim();
    if (sentence.length === 0 || thinking) return;
    setNote(null);
    const proposal = parseEventOutputSchema.safeParse(
      await ask(sentence, quickAddContext(new Date())),
    );
    if (!proposal.success) return;
    onFill(draftPatchFromProposal(proposal.data, sentence, members));
    setNote(quickAddNote(proposal.data));
    setBurst((count) => count + 1);
  };

  const message =
    state.phase === 'napping'
      ? "The assistant's napping — fill it in below instead."
      : state.phase === 'failed' || (state.phase === 'ready' && note === null)
        ? "Couldn't make an event out of that — fill it in below instead."
        : note;

  return (
    <div className="mb-5 rounded-md border border-line bg-bg-2 p-3" data-testid="quick-add">
      {/* A little one (Dan, 2026-10-11): a sentence turning into an event is worth a pop. */}
      {burst > 0 && <Fireworks key={burst} size="small" onDone={() => setBurst(0)} />}
      <label
        htmlFor="quick-add-input"
        className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-3"
      >
        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
        Just say it
      </label>
      <div className="flex items-end gap-2">
        <textarea
          id="quick-add-input"
          value={text}
          rows={2}
          data-testid="quick-add-input"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder="Soccer practice Thursday 4 to 5:30 at the rec center"
          className="min-w-0 flex-1 resize-none rounded-md border border-line bg-bg p-3 text-base text-fg outline-none focus:border-line-strong"
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={thinking || text.trim().length === 0}
          data-testid="quick-add-submit"
          className="h-12 shrink-0 rounded-md bg-inverse px-4 text-sm font-semibold text-inverse-fg disabled:opacity-60"
        >
          {thinking ? 'Reading…' : 'Fill in'}
        </button>
      </div>
      {message && (
        <p className="pt-2 text-[12px] text-fg-3" data-testid="quick-add-note" aria-live="polite">
          {message}
        </p>
      )}
    </div>
  );
}
